// Pure helpers for the meeting room page (pages/RoomPage.tsx): no React, no LiveKit, so tests reach them directly.
import type { MeetingSegment } from '@jabberr/core';
import { ApiError } from './api.js';
import { BOT_IDENTITY } from '../pages/RoomPanel.js';

export function peerKeys(participants: { identity: string; name?: string }[]): string[] {
  return participants.map((p) => (p.identity === BOT_IDENTITY ? BOT_IDENTITY : p.name || p.identity));
}

/** Format a meeting segment timestamp to a relative string (seconds since start). */
export function formatSegmentTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Format elapsed time since startedAt to HH:MM or MM:SS. */
export function formatElapsed(startedAt: string): string {
  const elapsedMs = Date.now() - new Date(startedAt).getTime();
  const totalSecs = Math.floor(elapsedMs / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hrs}:${remainingMins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Pure helper: decide which side panels are open given viewport and toggles.
 * At 1200px and above both may show; below 1200px one at a time (transcript wins);
 * below 800px each is a full screen sheet, still one at a time. */
export function resolveSidePanels(viewportWidth: number, showPeople: boolean, showTranscript: boolean): 'people' | 'transcript' | 'both' | 'none' {
  if (viewportWidth >= 1200) {
    if (showPeople && showTranscript) return 'both';
    if (showPeople) return 'people';
    if (showTranscript) return 'transcript';
    return 'none';
  }
  if (showTranscript) return 'transcript';
  if (showPeople) return 'people';
  return 'none';
}

/** Why a join failed, from the error the browser or the api gave: a refused microphone, an ended meeting,
 *  a tab from before an update (its routes are gone), or the room itself. */
export function joinFailure(e: unknown): 'mic-denied' | 'ended' | 'stale' | 'unavailable' {
  if (e instanceof ApiError) {
    if (e.status === 409) return 'ended';
    if (e.status === 404 || e.status === 410) return 'stale';
    return 'unavailable';
  }
  const name = e instanceof Error ? `${e.name} ${e.message}` : '';
  if (/NotAllowed|Permission|denied/i.test(name)) return 'mic-denied';
  return 'unavailable';
}

/** Pure helper: 'ask' when the person is the last human present and may end the meeting, else 'leave'. */
export function leaveChoice(humansPresent: number, canEnd: boolean): 'ask' | 'leave' {
  return humansPresent <= 1 && canEnd ? 'ask' : 'leave';
}

export function canInviteFromSegments(segments: Pick<MeetingSegment, 'speaker' | 'userId' | 'text'>[], canAnswerSharedInput: boolean): boolean {
  return segments.some(segment => !!segment.text.trim() && (!!segment.userId || (canAnswerSharedInput && segment.speaker === 'Shared microphone')));
}
